import type { CustomObjectField } from '@kizen/api/custom-object';

import styled from '@emotion/styled';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissionValue } from '@kizen/permissions/hooks/usePermissionValue';
import { getStreamKey } from '@kizen/permissions/utils';
import { truncateCss } from 'app/typography';
import { TextEllipsisWithTooltip } from '__components/Kizen/Table';
import { PermissionRangeInput } from './PermissionRangeInput';
import { GridContainer } from './styled';
import { Tooltip } from './Tooltip';

type Field = SnakeToCamelCase<CustomObjectField>;

type PermissionLabelProps = {
  permissionKey: string;
  label: string;
  enableRelatedFieldRule?: boolean;
  isRelatedField?: boolean;
};

type FieldPermissionRowsProps = {
  categoryName: string;
  sectionId: string;
  fields: Field[];
  enableRelatedFieldRule?: boolean;
};

const InputContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  margin-bottom: 15px;
`;

const RowLabel = styled(TextEllipsisWithTooltip)`
  ${truncateCss}
  margin-left: 40px;
  margin-bottom: 15px;
`;

const CategoryLabel = styled(TextEllipsisWithTooltip)`
  ${truncateCss}
  margin-left: 20px;
  margin-bottom: 15px;
`;

const LabelContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 5px;
`;

const PermissionLabel = (props: PermissionLabelProps) => {
  const {
    label,
    permissionKey,
    isRelatedField = false,
    enableRelatedFieldRule = true,
  } = props;
  const { t } = useTranslation();
  const tooltip = usePermissionValue(permissionKey, ({ tooltip }) => tooltip);
  const tt = tooltip
    ? tooltip
    : enableRelatedFieldRule && isRelatedField
      ? t(
          'This permission group has all access to the related Object, so there are no restrictions.'
        )
      : null;

  return (
    <LabelContainer>
      <RowLabel>{label}</RowLabel>
      <Tooltip text={tt} show={Boolean(tt)} />
    </LabelContainer>
  );
};

export const FieldPermissionRows = (props: FieldPermissionRowsProps) => {
  const { categoryName, sectionId, fields, enableRelatedFieldRule } = props;

  return (
    <GridContainer>
      <CategoryLabel color="var(--color-font-primary)" weight="bold">
        {categoryName}
      </CategoryLabel>
      <div />
      {fields.map(({ displayName, name, relation }) => {
        const key = getStreamKey(name, sectionId);
        return (
          <Fragment key={key}>
            <PermissionLabel
              label={displayName}
              permissionKey={key}
              isRelatedField={Boolean(relation?.relatedObject)}
              enableRelatedFieldRule={enableRelatedFieldRule}
            />
            <InputContainer>
              <PermissionRangeInput permissionKey={key} affordance="range" />
            </InputContainer>
          </Fragment>
        );
      })}
    </GridContainer>
  );
};
